import React from 'react';
import { motion } from 'framer-motion';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolvePublicMediaUrl } from '../utils/mediaUrl';

const coerceBool = (v, defaultValue = true) => {
  if (v === undefined || v === null || v === '') return defaultValue;
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1') return true;
  if (v === 0 || v === '0') return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  return defaultValue;
};

const resolveLeaderImage = (url) => {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^(data:|https?:|blob:)/i.test(s)) return s;
  if (s.startsWith('/images/')) return s;
  if (s.startsWith('images/')) return `/${s}`;
  return resolvePublicMediaUrl(s);
};

const normalizeMembers = (school) => {
  const raw = school?.LEADERSHIP_MEMBERS;
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch (e) {
      // ignore
    }
  }

  if (list.length > 0) {
    return list
      .map((m) => ({
        name: m?.name || '',
        designation: m?.designation || '',
        message: m?.message || '',
        imageUrl: m?.imageUrl || m?.image || '',
        isActive: coerceBool(m?.isActive, true),
        showOnHome: coerceBool(m?.showOnHome, true),
        showOnAbout: coerceBool(m?.showOnAbout, true),
      }))
      .filter((m) => m.isActive)
      .filter((m) => m.name || m.message || m.imageUrl);
  }

  // No members configured => hide section (dynamic-only behavior)
  return [];
};

export default function LeadershipSection({ context = 'home', title = 'Leadership Message' }) {
  const { school } = useSiteSettings();
  if (school?.SHOW_LEADERSHIP_MESSAGE === false) return null;

  const members = normalizeMembers(school).filter((m) => {
    if (context === 'about') return m.showOnAbout !== false;
    return m.showOnHome !== false;
  });

  if (!members.length) return null;

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-bold font-display text-blue-900 text-center mb-12">
          {title}
        </h2>

        <div
          className={[
            'gap-8 mx-auto',
            members.length === 1 ? 'max-w-3xl grid grid-cols-1' : 'max-w-5xl grid grid-cols-1 md:grid-cols-2',
          ].join(' ')}
        >
          {members.map((leader, index) => (
            <motion.div
              key={`${leader.name}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all"
            >
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-1/3">
                  <img
                    src={resolveLeaderImage(leader.imageUrl) || '/images/director.png'}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 sm:w-2/3">
                  <h3 className="text-xl font-bold text-blue-900 mb-1">{leader.name}</h3>
                  {!!leader.designation && (
                    <p className="text-accent font-semibold text-sm mb-3">{leader.designation}</p>
                  )}
                  {!!leader.message && (
                    <p className="text-gray-700 text-sm leading-relaxed italic">"{leader.message}"</p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

